import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import useDebounce from 'hooks/useDebounce';

import { BondTransactionActionEnum } from 'global/Securities/types/api/bond';

import { BondsModalTypeEnum, MAX_UNIT_AMOUNT } from '../constants';
import { useBondPreviewStore } from '../stores/useBondPreview';
import { useBondsModalStore } from '../stores/useBondsModalStore';

const DEBOUNCE_DURATION = 500; // 500ms

interface Params {
  action: BondTransactionActionEnum;
  productId: string;
  maxUnit?: number;
  orderId?: string;
}

export default function useBondPreviewForm({
  action,
  productId,
  orderId = '',
}: Params) {
  const isBuy = action === BondTransactionActionEnum.BUY;
  const isSell = action === BondTransactionActionEnum.SELL;

  const [unit, setUnit] = useState<number | undefined>();
  const [error, setError] = useState('');
  const debouncedUnit = useDebounce(unit, DEBOUNCE_DURATION);

  const [loadBuyPreview, loadSellPreview, isLoading, buy, sell] =
    useBondPreviewStore((s) => [
      s.loadBuyPreview,
      s.loadSellPreview,
      s.isLoading,
      s.buy,
      s.sell,
    ]);

  const [modalType] = useBondsModalStore((s) => [s.modalType]);

  const isDisabled = isLoading || !debouncedUnit || debouncedUnit <= 0;

  const handleUnitAmountChanged = (e: ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value, 10);

    // prevent NaN || handle negative value
    if (Number.isNaN(value)) {
      setUnit(undefined);
      return;
    }

    // handle unit more than maximum
    if (isBuy && value >= MAX_UNIT_AMOUNT) {
      setUnit(MAX_UNIT_AMOUNT);
      return;
    }

    if (isSell && value >= sell?.available_units) {
      setUnit(sell?.available_units);
      return;
    }

    setUnit(value);
  };

  const clearError = () => {
    setError('');
  };

  const loadPreview = useCallback(() => {
    // prevent NaN || handle negative value
    if (Number.isNaN(debouncedUnit)) {
      setError('Unit must be an integer');
      return;
    }

    if (debouncedUnit < 0) {
      setError('Unit cannot be a negative number.');
      return;
    }

    if (!productId) {
      setError('Product is required');
      return;
    }

    if (!productId) {
      setError('Product id is required');
      return;
    }

    if (isBuy) {
      loadBuyPreview(productId, unit);
      return;
    }

    if (!orderId) {
      setError('Order id is required');
      return;
    }

    if (isSell) {
      loadSellPreview(productId, orderId, unit);
    }
  }, [debouncedUnit, isBuy, isSell, productId, orderId]);

  useEffect(() => {
    if (
      // prevent calling when loading / fetching
      isLoading ||
      // prevent calling if the modalType is not enum
      modalType !== BondsModalTypeEnum.TRANSACTION ||
      // prevent calling if action is unspecified
      action === BondTransactionActionEnum.UNSPECIFIED
    ) {
      // do nothing
      return;
    }

    loadPreview();
  }, [debouncedUnit, productId, modalType, action]);

  return {
    preview: {
      buy,
      sell,
    },
    handleUnitAmountChanged,
    unit,
    isLoadingPreview: isLoading,
    error,
    clearError,
    isDisabled,
  };
}
